import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectClustersType } from 'shared/map-search/selectors/clustersType';
import { selectActiveFeature } from 'shared/map-search/selectors/features';
import { EClustersType } from 'shared/map-search/types/clustersType';
import { TFeature } from 'shared/map-search/types/map';

import { useFeaturesContext } from '../../context';

interface IUseBalloonPromoHandlersProps {
  onFeatureMouseEnter: (feature: TFeature) => void;
  onFeatureMouseLeave: () => void;
  hoveredFeatureRef: React.RefObject<TFeature | null>;
  isRedesign: boolean;
}

export const useBalloonPromoHandlers = ({
  hoveredFeatureRef,
  onFeatureMouseEnter,
  onFeatureMouseLeave,
  isRedesign,
}: IUseBalloonPromoHandlersProps) => {
  const { balloonPromoManager } = useFeaturesContext();

  const clustersType = useSelector(selectClustersType);
  const selectedFeature = useSelector(selectActiveFeature);

  const lastSelectedFeatureRef = useRef<TFeature | null>(selectedFeature);

  const handleBalloonPromoMouseEnter = useCallback(
    (event: YMaps.IEvent) => {
      const featureByBalloon = balloonPromoManager.objectManager.objects.getById<TFeature>(
        event.get<string>('objectId'),
      );
      const lastSelectedFeature = lastSelectedFeatureRef.current;

      if (!featureByBalloon || lastSelectedFeature?.id === featureByBalloon.id) {
        return;
      }

      onFeatureMouseEnter(featureByBalloon);

      balloonPromoManager.updateBalloonProperties(featureByBalloon.id, {
        ...featureByBalloon.properties,
        isHovered: true,
      });
    },
    [balloonPromoManager, onFeatureMouseEnter],
  );

  const handleBalloonPromoMouseLeave = useCallback(() => {
    const hoveredFeature = hoveredFeatureRef.current;

    if (!hoveredFeature) {
      return;
    }

    onFeatureMouseLeave();

    balloonPromoManager.updateBalloonProperties(hoveredFeature.id, {
      ...hoveredFeature.properties,
      isHovered: false,
    });
  }, [balloonPromoManager, onFeatureMouseLeave, hoveredFeatureRef]);

  useEffect(() => {
    if (!clustersType && !isRedesign) {
      return () => {};
    }

    balloonPromoManager.objectManager.objects.events.add('mouseenter', handleBalloonPromoMouseEnter);
    balloonPromoManager.objectManager.objects.events.add('mouseleave', handleBalloonPromoMouseLeave);

    return () => {
      balloonPromoManager.objectManager.objects.events.remove('mouseenter', handleBalloonPromoMouseEnter);
      balloonPromoManager.objectManager.objects.events.remove('mouseleave', handleBalloonPromoMouseLeave);
    };
  }, [clustersType, balloonPromoManager, handleBalloonPromoMouseEnter, handleBalloonPromoMouseLeave, isRedesign]);

  useEffect(() => {
    if (!selectedFeature || clustersType !== EClustersType.OpenablePeas) {
      return;
    }

    const lastSelectedFeature = lastSelectedFeatureRef.current;

    if (
      lastSelectedFeature &&
      lastSelectedFeature.id !== selectedFeature.id &&
      balloonPromoManager.objectManager.objects.getById<TFeature>(lastSelectedFeature.id)
    ) {
      balloonPromoManager.objectManager.remove([lastSelectedFeature.id]);
    }

    lastSelectedFeatureRef.current = selectedFeature;
  }, [selectedFeature, clustersType, balloonPromoManager]);
};
